// Global Types
import type {
  $Children,
  $PaymentIntent,
} from 'common.types';
import type {
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';
import type {
  Stripe,
  StripeElements,
} from '@stripe/stripe-js';

// Global Enums
import {
  ErrorKey,
} from 'common.enums';
import {
  RegattaPaymentStatus,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import {
  CardElement,
  Elements,
  ElementsConsumer,
} from '@stripe/react-stripe-js';
import {
  loadStripe,
} from '@stripe/stripe-js';
import {
  Loader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  CustomError,
} from '@vroskus/library-error';
import config from '../../../config';

// Components
import InputDiscount from '../../form/InputDiscount';

// Services
import Services from '../../../services';

// Types
type $OwnProps = {
  children: $Children;
  id: string;
  itemId: string;
};

type $BareProps = $OwnProps & $Translate;

type $Props = $BareProps & {
  elements: StripeElements;
  stripe: Stripe;
};

type $State = {
  error: Error | null;
  paying: boolean;
  regattaPayment: $Model<'RegattaPayment'> | null;
  regattaPaymentDiscount: $Model<'RegattaPaymentDiscount'> | null;
  visible: boolean;
};

class PaymentDialogView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      error: null,
      paying: false,
      regattaPayment: null,
      regattaPaymentDiscount: null,
      visible: false,
    };
  }

  componentDidMount(): void {
    this.findOneRegattaPayment();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async setRegattaPaymentDiscount(
    regattaPaymentDiscount: $Model<'RegattaPaymentDiscount'>,
  ): Promise<void> {
    const {
      regattaPayment,
    } = this.state;

    try {
      if (regattaPayment !== null) {
        const upsertRegattaPaymentRequest = {
          ...regattaPayment,
          RegattaPaymentDiscountId: regattaPaymentDiscount.id,
        };

        const updatedRegattaPayment = await Services.Api.upsertRegattaPayment(
          upsertRegattaPaymentRequest,
        );

        await this.setStateAsync({
          regattaPayment: updatedRegattaPayment,
        });
      }

      await this.setStateAsync({
        regattaPaymentDiscount,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);

      await this.setStateAsync({
        error: error as Error,
      });
    }
  }

  async findOneRegattaPayment(): Promise<void> {
    const {
      itemId,
    } = this.props;

    try {
      const findRegattaPaymentRequest = {
        RegattaId: itemId,
      };

      let regattaPayment: $Model<'RegattaPayment'> | null = null;

      const foundRegattaPayment = await Services.Api.findOneRegattaPayment(
        findRegattaPaymentRequest,
      );

      if (foundRegattaPayment !== null) {
        regattaPayment = foundRegattaPayment;
      } else {
        const upsertRegattaPayment = {
          PaymentId: '',
          PaymentIntent: '',
          RegattaId: itemId,
          RegattaPaymentDiscountId: null,
          Status: RegattaPaymentStatus.intent,
        };

        regattaPayment = await Services.Api.upsertRegattaPayment(upsertRegattaPayment);
      }

      if (regattaPayment.RegattaPaymentDiscountId) {
        const getRegattaPaymentDiscountRequest = {
          id: regattaPayment.RegattaPaymentDiscountId,
        };

        const regattaPaymentDiscount = await Services.Api.getOneRegattaPaymentDiscount(
          getRegattaPaymentDiscountRequest,
        );

        await this.setStateAsync({
          regattaPaymentDiscount,
        });
      }

      await this.setStateAsync({
        regattaPayment,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);

      await this.setStateAsync({
        error: error as Error,
      });
    }
  }

  toggle(): void {
    this.setState((prevState: $State) => ({
      visible: !prevState.visible,
    }));
  }

  childrenWithToggleHandler() {
    const {
      children,
    } = this.props;

    if (React.isValidElement(children)) {
      return React.cloneElement(
        children,
        {
          // @ts-expect-error dialog
          onClick: () => this.toggle(),
        },
      );
    }

    return children;
  }

  async makePayment(paymentIntent: $PaymentIntent) {
    const {
      elements,
      stripe,
    } = this.props;

    if (paymentIntent.clientSecret === null) {
      throw new CustomError(
        'Client secret not set',
        ErrorKey.paymentError,
        {
          data: {
            paymentIntent,
          },
        },
      );
    }

    const card = elements.getElement(CardElement);

    if (card === null) {
      throw new Error('Card element not found');
    }

    return stripe.confirmCardPayment(
      paymentIntent.clientSecret,
      {
        payment_method: {
          card,
        },
      },
    );
  }

  async pay(e): Promise<void> {
    const {
      regattaPayment,
    } = this.state;

    try {
      e.preventDefault();
      e.stopPropagation();

      await this.setStateAsync({
        error: null,
        paying: true,
      });

      if (regattaPayment !== null) {
        const paymentIntent: $PaymentIntent = JSON.parse(regattaPayment.PaymentIntent);

        const payment = await this.makePayment(paymentIntent);

        const paymentStatus: string | undefined = _.get(
          payment,
          'paymentIntent.status',
        );

        if (paymentStatus === 'succeeded') {
          const request = {
            ...regattaPayment,
            Status: RegattaPaymentStatus.pending,
          };

          const updatedRegattaPayment = await Services.Api.upsertRegattaPayment(request);

          await this.setStateAsync({
            regattaPayment: updatedRegattaPayment,
          });
        }

        const {
          error,
        } = payment;

        if (error) {
          throw error;
        }
      }

      await this.setStateAsync({
        paying: false,
      });
    } catch (error) {
      const errorMessage: string = _.get(
        error,
        'message',
        'Unknown message',
      );

      const customError = new CustomError(
        errorMessage,
        ErrorKey.paymentError,
        {
          data: {
            error,
          },
        },
      );

      ErrorLog.exception(customError);

      await this.setStateAsync({
        error: error as Error,
        paying: false,
      });
    }
  }

  renderForm() {
    const {
      t,
    } = this.props;
    const {
      error,
      paying,
      regattaPayment,
      regattaPaymentDiscount,
    } = this.state;

    if (regattaPayment !== null) {
      const paymentIntent: $PaymentIntent = JSON.parse(regattaPayment.PaymentIntent);

      return (
        <form
          id={'payment'}
          onSubmit={(e) => this.pay(e)}
        >
          <ModalBody>
            <Row>
              <Col
                sm={7}
                xs={5}
              >
                <div className={'mb-3'}>
                  <Label className={'mb-0'}>
                    {t('Price')}
                  </Label>
                  <div className={'h2 text-uppercase text-success mb-0'}>
                    {`${paymentIntent.currency} ${paymentIntent.amount / 100}`}
                  </div>
                  {regattaPaymentDiscount !== null && (
                    <div>
                      <span>
                        {`${t('Discount')}: `}
                      </span>
                      <span className={'text-uppercase text-bold'}>
                        {`${paymentIntent.currency} ${regattaPaymentDiscount.Value / 100}`}
                      </span>
                    </div>
                  )}
                </div>
              </Col>
              <Col
                sm={5}
                xs={7}
              >
                <div className={'mb-3'}>
                  <InputDiscount
                    label={t('Discount code')}
                    onFound={(item) => this.setRegattaPaymentDiscount(item)}
                    regattaPaymentDiscount={regattaPaymentDiscount}
                  />
                </div>
              </Col>
            </Row>
            <div className={'text-muted mb-3'}>
              {t('Purchase includes enabled regatta registration feature without any limitations')}
            </div>
            <div>
              <Label>
                {t('Pay with the card')}
              </Label>
              <div className={'form-control'}>
                <CardElement />
              </div>
            </div>
            {error !== null && (
              <div className={'text-danger mt-2'}>
                {error.message}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            {regattaPayment.Status === RegattaPaymentStatus.intent && (
              <Button
                color={'warning'}
                disabled={paying}
                form={'payment'}
                type={'submit'}
              >
                {t('Pay')}
              </Button>
            )}
            <Button
              color={'secondary'}
              onClick={() => this.toggle()}
            >
              {t('Close')}
            </Button>
          </ModalFooter>
        </form>
      );
    }

    return null;
  }

  renderSuccess() {
    const {
      t,
    } = this.props;

    return (
      <>
        <ModalBody>
          <div className={'text-center'}>
            <em className={'fa fa-5x icon-check text-success mb-3'} />
            <div>
              {`${t('Payment successful')}!`}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color={'secondary'}
            onClick={() => this.toggle()}
          >
            {t('Close')}
          </Button>
        </ModalFooter>
      </>
    );
  }

  render() {
    const {
      id,
      t,
    } = this.props;
    const {
      regattaPayment,
      visible,
    } = this.state;

    return (
      <>
        {this.childrenWithToggleHandler()}
        <Modal
          centered
          id={id}
          isOpen={visible}
          toggle={() => this.toggle()}
        >
          <ModalHeader>
            <p className={'mb-0'}>
              {t('Payment')}
            </p>
          </ModalHeader>
          <Loader
            loading={regattaPayment === null}
            overlay
          >
            {regattaPayment !== null ? (
              <>
                {regattaPayment.Status === RegattaPaymentStatus.intent && this.renderForm()}
                {regattaPayment.Status === RegattaPaymentStatus.pending && this.renderSuccess()}
              </>
            ) : null}
          </Loader>
        </Modal>
      </>
    );
  }
}

const Component = function ({
  children,
  id,
  itemId,
  t,
}: $BareProps) {
  if (config.STRIPE_PUBLISHABLE_KEY !== '') {
    const stripePromise = loadStripe(config.STRIPE_PUBLISHABLE_KEY);

    return (
      <Elements stripe={stripePromise}>
        <ElementsConsumer>
          {({
            elements,
            stripe,
          }) => (elements !== null && stripe !== null) && (
            <PaymentDialogView
              elements={elements}
              id={id}
              itemId={itemId}
              stripe={stripe}
              t={t}
            >
              {children}
            </PaymentDialogView>
          )}
        </ElementsConsumer>
      </Elements>
    );
  }

  return null;
};

export default Component;
